import { useQuery } from "@apollo/client";
import { compact } from "lodash";
import {
  Route,
  Switch,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { GetProfileQueryQueryVariables } from "types/graphql-schema";
import { GetProfileQueryQuery } from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
} from "@cache/cache";
import Button from "@components/button/button";
import Layout from "@components/layout/layout";
import AppLink from "@components/link/link";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import useUserComboboxQuery from "@components/user-combobox/use-user-combobox-query";
import UserCombobox from "@components/user-combobox/user-combobox";
import {
  UserComboboxOption,
  UserComboboxOptionType,
} from "@components/user-combobox/user-combobox-list";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull, getUrlUsingTypename } from "@helpers/helpers";

import ProfileCareer from "./career-tab/profile-career-tab";
import getProfileQuery from "./graphql/get-profile-query";
import ProfileEmptyState from "./profile-empty-state";
import ProfileOverview from "./profile-overview";
import ProfilePrivateNotes from "./profile-private-notes";
import ProfileWorkHistory from "./profile-work-history";

const ProfilePage = () => {
  const { path } = useRouteMatch();
  const link = useLink();
  const location = useLocation<TFLocationState>();
  const { profileId: profileIdStr } = useParams<{
    profileId: string | undefined;
  }>();
  const currentUser = currentUserVar();
  const organization = currentOrganizationVar();
  const profileId = profileIdStr ? parseInt(profileIdStr) : undefined;

  const { data, loading } = useQuery<
    GetProfileQueryQuery,
    GetProfileQueryQueryVariables
  >(getProfileQuery, {
    variables: { userId: profileId || -1 },
    skip: !profileId,
  });
  const profile = data?.user;
  const isManager = profile?.managersList.some(
    ({ id }) => id === currentUser.id,
  );

  const { options, query, setQuery } = useUserComboboxQuery({
    types: [UserComboboxOptionType.USER],
    selected: profile
      ? {
          id: profile.id,
          name: profile.name,
          type: UserComboboxOptionType.USER,
        }
      : null,
  });
  const selectedOption =
    options.find((option) => option.id === profile?.id) || null;

  const handleChangeValue = (option: UserComboboxOption) => {
    if (option.type === UserComboboxOptionType.USER) {
      const user = option;
      link.redirect(getUrlUsingTypename({ ...user, __typename: "UserNode" }));
    }
  };

  const tabClassName =
    "pb-4 inline-block text-sm text-gray-400 border-b-2 font-medium border-transparent -mb-px";
  const activeTabClassName = "border-blue-link text-gray-700 ";

  const isAdmin = isAdminVar();
  const canViewCareerTab =
    profile &&
    (isAdmin ||
      profile.isInCurrentUserReportingTree ||
      currentUser.id === profile.id);

  const directReports = assertEdgesNonNull(currentUser.directReports);
  const currentUserIsManagerOfProfile = directReports.find(
    ({ id }) => id === profileId,
  );
  const showPrivateNotes =
    currentUserIsManagerOfProfile && waffle.flag_is_active("private-notes");

  return (
    <Layout>
      <Layout.Header
        breadcrumbs={compact([
          {
            title: "Profile",
            url: getUrlUsingTypename(currentUser) as string,
          },
          profile && {
            title: (
              <UserCombobox
                value={selectedOption}
                options={options}
                query={query}
                onChangeValue={handleChangeValue}
                onChangeQuery={setQuery}
              />
            ),
            url: getUrlUsingTypename(profile) as string,
          },
        ])}
      >
        <Button to={`/org-chart`} text="Org Chart" />
      </Layout.Header>
      <Layout.Container>
        <Layout.Main width="full">
          {loading ? (
            <Loading>Loading profile...</Loading>
          ) : !profile ? (
            <div>
              <ProfileEmptyState>Profile not found.</ProfileEmptyState>
            </div>
          ) : (
            <div>
              {/* Work history might contain sensitive information, so we only show it to the user themselves */}
              {(profileId === currentUser.id || isManager) && (
                <div className="border-b flex gap-6 mb-6">
                  <AppLink
                    to={`/profile/${profileId}`}
                    className={classNames(
                      tabClassName,
                      location.pathname === `/profile/${profileId}`
                        ? activeTabClassName
                        : "",
                    )}
                  >
                    Overview
                  </AppLink>
                  {organization.featureFlags.workHistory && (
                    <AppLink
                      to={`/profile/${profileId}/work-history`}
                      className={classNames(
                        tabClassName,
                        location.pathname.includes(
                          `/profile/${profileId}/work-history`,
                        )
                          ? activeTabClassName
                          : "",
                      )}
                    >
                      Work History
                    </AppLink>
                  )}
                  {canViewCareerTab && (
                    <AppLink
                      to={`/profile/${profileId}/career`}
                      className={classNames(
                        tabClassName,
                        location.pathname.includes(
                          `/profile/${profileId}/career`,
                        )
                          ? activeTabClassName
                          : "",
                      )}
                    >
                      Career
                    </AppLink>
                  )}
                  {showPrivateNotes && (
                    <AppLink
                      to={`/profile/${profileId}/private-notes`}
                      className={classNames(
                        tabClassName,
                        location.pathname.includes(
                          `/profile/${profileId}/private-notes`,
                        )
                          ? activeTabClassName
                          : "",
                      )}
                    >
                      Private Notes
                    </AppLink>
                  )}
                </div>
              )}
              <Switch>
                <Route exact path={path}>
                  <ProfileOverview profileId={profile.id} />
                </Route>
                <Route exact path={`${path}/work-history`}>
                  <ProfileWorkHistory profileId={profile.id} />
                </Route>
                {canViewCareerTab && (
                  <Route exact path={`${path}/career`}>
                    <ProfileCareer profileId={profile.id} />
                  </Route>
                )}
                {showPrivateNotes && (
                  <Route exact path={`${path}/private-notes`}>
                    <ProfilePrivateNotes profileId={profile.id} />
                  </Route>
                )}
              </Switch>
            </div>
          )}
        </Layout.Main>
      </Layout.Container>
    </Layout>
  );
};

export default ProfilePage;
